"use client"
import { useCallback, useEffect, useRef, useState } from 'react'

type UseRefetchOnWindowFocusProps<T> = {
  fetchFn: () => Promise<T> | void
  enabled?: boolean
  onSuccess?: (data: T) => void
  onError?: (error: unknown) => void
}

export function useRefetchOnWindowFocus<T>({
  fetchFn,
  enabled = true,
  onSuccess,
  onError,
}: UseRefetchOnWindowFocusProps<T>) {
  const isFetching = useRef(false)
  const [isLoading, setIsLoading] = useState(false)

  const refetch = useCallback(async () => {
    if (isFetching.current || !enabled) return
    try {
      isFetching.current = true
      setIsLoading(true)
      const data = await fetchFn()
      if (onSuccess && data !== undefined) onSuccess(data)
    } catch (error) {
      if (onError) onError(error)
    } finally {
      isFetching.current = false
      setIsLoading(false)
    }
  }, [fetchFn, enabled, onSuccess, onError])

  useEffect(() => {
    if (!enabled) return
    const handleFocus = () => {
      refetch()
    }

    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [enabled, refetch])

  return { refetch, isLoading }
}
